/**
 * @file Frontend API boundary for the authoritative Compliance register.
 */

import {
    changeComplianceCategoryLifecycleCallable,
    changeComplianceRequirementLifecycleCallable,
    changeComplianceTrackedItemLifecycleCallable,
    correctComplianceRecordCallable,
    createComplianceEvidenceUploadCallable,
    createComplianceRecordCallable,
    createComplianceRequirementCallable,
    createComplianceTrackedItemCallable,
    deleteComplianceRecordCallable,
    deleteComplianceEvidenceCallable,
    finalizeComplianceEvidenceUploadCallable,
    generateComplianceReportCallable,
    getComplianceBootstrapCallable,
    getComplianceNotificationTargetCallable,
    getComplianceEvidenceOwnerCallable,
    getComplianceEvidenceReadUrlCallable,
    initializeComplianceProtectedCategoriesCallable,
    listComplianceRecordHistoryCallable,
    listComplianceRequirementsCallable,
    listComplianceServiceHistoryCallable,
    listComplianceSummariesCallable,
    repairComplianceProjectionsCallable,
    saveComplianceCategoryConfigCallable,
    saveComplianceTrackedItemCallable,
    updateComplianceEvidenceMetadataCallable,
} from "../../app/firebase/callables.js";

async function invokeComplianceCallable(callable, payload) {
    const result = await callable(payload);
    return result.data;
}

/**
 * Loads the read-only Compliance route bootstrap.
 * @returns {Promise<object>} Authorized category bootstrap.
 */
export async function getComplianceBootstrap() {
    return invokeComplianceCallable(getComplianceBootstrapCallable, {});
}

/**
 * Resolves one notification against current Compliance/category access.
 * @param {string} notificationId Owned notification ID.
 * @returns {Promise<object>} Authorized record target or informational result.
 */
export async function getComplianceNotificationTarget(notificationId) {
    return invokeComplianceCallable(
        getComplianceNotificationTargetCallable,
        {notificationId},
    );
}

/**
 * Saves one bounded Compliance category configuration record.
 * @param {object} payload Frozen Phase 2 category request.
 * @returns {Promise<object>} Authoritative category response.
 */
export async function saveComplianceCategoryConfig(payload) {
    return invokeComplianceCallable(saveComplianceCategoryConfigCallable, payload);
}

/**
 * Creates only missing protected People/Assets categories from explicit admin
 * settings.
 * @param {object} payload Explicit missing protected-category rows.
 * @returns {Promise<object>} Authoritative category response.
 */
export async function initializeComplianceProtectedCategories(payload) {
    return invokeComplianceCallable(
        initializeComplianceProtectedCategoriesCallable,
        payload,
    );
}

/**
 * Applies an immediate Compliance category lifecycle action.
 * @param {object} payload Frozen Phase 2 lifecycle request.
 * @returns {Promise<object>} Authoritative category response.
 */
export async function changeComplianceCategoryLifecycle(payload) {
    return invokeComplianceCallable(changeComplianceCategoryLifecycleCallable, payload);
}

/**
 * Creates one manual tracked item.
 * @param {object} payload Frozen Phase 2 create request.
 * @returns {Promise<object>} Authoritative tracked item.
 */
export async function createComplianceTrackedItem(payload) {
    return invokeComplianceCallable(createComplianceTrackedItemCallable, payload);
}

/**
 * Saves one manual tracked item.
 * @param {object} payload Frozen Phase 2 save request.
 * @returns {Promise<object>} Authoritative tracked item.
 */
export async function saveComplianceTrackedItem(payload) {
    return invokeComplianceCallable(saveComplianceTrackedItemCallable, payload);
}

/**
 * Applies one manual tracked-item lifecycle action.
 * @param {object} payload Frozen Phase 2 lifecycle request.
 * @returns {Promise<object>} Authoritative tracked item response.
 */
export async function changeComplianceTrackedItemLifecycle(payload) {
    return invokeComplianceCallable(changeComplianceTrackedItemLifecycleCallable, payload);
}

/**
 * Creates one neutral requirement shell.
 * @param {object} payload Frozen Phase 2 create request.
 * @returns {Promise<object>} Authoritative requirement, parent item, and tokens.
 */
export async function createComplianceRequirement(payload) {
    return invokeComplianceCallable(createComplianceRequirementCallable, payload);
}

/**
 * Applies one requirement close/reopen/delete action.
 * @param {object} payload Frozen Phase 2 lifecycle request.
 * @returns {Promise<object>} Authoritative requirement and item.
 */
export async function changeComplianceRequirementLifecycle(payload) {
    return invokeComplianceCallable(changeComplianceRequirementLifecycleCallable, payload);
}

/**
 * Creates one attachment-empty manual Compliance record.
 * @param {object} payload Frozen Phase 2 create request.
 * @returns {Promise<object>} Rebuilt record, requirement, and item.
 */
export async function createComplianceRecord(payload) {
    return invokeComplianceCallable(createComplianceRecordCallable, payload);
}

/**
 * Corrects the elected current manual Compliance record.
 * @param {object} payload Frozen Phase 2 correction request.
 * @returns {Promise<object>} Rebuilt record, requirement, and item.
 */
export async function correctComplianceRecord(payload) {
    return invokeComplianceCallable(correctComplianceRecordCallable, payload);
}

/**
 * Deletes the freshly confirmed current manual Compliance record.
 * @param {object} payload Frozen Phase 2 deletion request.
 * @returns {Promise<object>} Promotion and rebuilt projections.
 */
export async function deleteComplianceRecord(payload) {
    return invokeComplianceCallable(deleteComplianceRecordCallable, payload);
}

/**
 * Lists a globally merged active or inactive tracked-item summary page.
 * @param {object} payload View and opaque cursor.
 * @returns {Promise<object>} Bounded summary page.
 */
export async function listComplianceSummaries(payload) {
    return invokeComplianceCallable(listComplianceSummariesCallable, payload);
}

/**
 * Lists one tracked item's on-demand requirement page.
 * @param {object} payload Item, view, and stable cursor.
 * @returns {Promise<object>} Bounded requirement page.
 */
export async function listComplianceRequirements(payload) {
    return invokeComplianceCallable(listComplianceRequirementsCallable, payload);
}

/**
 * Lists one requirement's deterministic record-history page.
 * @param {object} payload Requirement identity and stable cursor.
 * @returns {Promise<object>} Bounded history page.
 */
export async function listComplianceRecordHistory(payload) {
    return invokeComplianceCallable(listComplianceRecordHistoryCallable, payload);
}

/**
 * Lists one derived requirement's bounded Maintenance Service history.
 * @param {object} payload Requirement identity and stable cursor.
 * @returns {Promise<object>} Source-safe Service rows and schedule summary.
 */
export async function listComplianceServiceHistory(payload) {
    return invokeComplianceCallable(listComplianceServiceHistoryCallable, payload);
}

/**
 * Dry-runs or applies one exact targeted projection repair.
 * @param {object} payload Frozen Phase 2 repair request.
 * @returns {Promise<object>} Before/after target results.
 */
export async function repairComplianceProjections(payload) {
    return invokeComplianceCallable(repairComplianceProjectionsCallable, payload);
}

/** Authorizes one exact Compliance evidence upload. */
export async function createComplianceEvidenceUpload(payload) {
    return invokeComplianceCallable(createComplianceEvidenceUploadCallable, payload);
}

/** Finalizes trusted Compliance evidence metadata after upload. */
export async function finalizeComplianceEvidenceUpload(payload) {
    return invokeComplianceCallable(finalizeComplianceEvidenceUploadCallable, payload);
}

/** Loads URL-free logical evidence for one Compliance owner. */
export async function getComplianceEvidenceOwner(payload) {
    return invokeComplianceCallable(getComplianceEvidenceOwnerCallable, payload);
}

/** Authorizes one short-lived Compliance evidence read. */
export async function getComplianceEvidenceReadUrl(payload) {
    return invokeComplianceCallable(getComplianceEvidenceReadUrlCallable, payload);
}

/** Updates one Compliance evidence attachment's display metadata. */
export async function updateComplianceEvidenceMetadata(payload) {
    return invokeComplianceCallable(updateComplianceEvidenceMetadataCallable, payload);
}

/** Deletes selected Compliance evidence metadata and Storage objects. */
export async function deleteComplianceEvidence(payload) {
    return invokeComplianceCallable(deleteComplianceEvidenceCallable, payload);
}

/** Generates one bounded authoritative Compliance report. */
export async function generateComplianceReport(payload) {
    return invokeComplianceCallable(generateComplianceReportCallable, payload);
}
